import { createContext, useContext, useMemo } from 'react';

import type { PropsWithChildren } from 'react';

/**
 * PanelScopeContext 값 타입
 */
interface PanelScopeContextValue {
  /**
   * 현재 Panel Scope의 키
   */
  scopeKey: string;
}

/**
 * PanelScopeContext
 * @description
 * ListScopeProvider와 DetailScopeProvider에서 scopeKey를 자식 컴포넌트에 전달하기 위한 Context입니다.
 * 하위 훅(usePanelActions 등)에서 scopeKey를 자동으로 획득할 수 있게 합니다.
 */
export const PanelScopeContext = createContext<PanelScopeContextValue | null>(null);

PanelScopeContext.displayName = 'PanelScopeContext';

/**
 * PanelScopeProvider Props
 */
interface PanelScopeProviderProps extends PropsWithChildren {
  /**
   * Scope 키
   */
  scopeKey: string;
}

/**
 * PanelScopeProvider
 * @description
 * PanelScopeContext를 제공하는 내부 Provider입니다.
 * ListScopeProvider와 DetailScopeProvider 내부에서 사용됩니다.
 */
export function PanelScopeProvider({ scopeKey, children }: PanelScopeProviderProps) {
  // useMemo로 참조 안정성 확보 (scopeKey가 변경될 때만 새 객체 생성)
  const value = useMemo(() => ({ scopeKey }), [scopeKey]);

  return <PanelScopeContext.Provider value={value}>{children}</PanelScopeContext.Provider>;
}

/**
 * 현재 Panel Scope의 scopeKey를 반환합니다.
 * @description
 * Provider 외부에서 호출하면 null을 반환합니다.
 * Phase 5의 usePanelActions에서 에러 처리를 담당합니다.
 * @returns PanelScopeContextValue | null
 * @example
 * ```tsx
 * const scopeContext = usePanelScopeContext();
 * if (scopeContext) {
 *   console.log(scopeContext.scopeKey);
 * }
 * ```
 */
export function usePanelScopeContext(): PanelScopeContextValue | null {
  return useContext(PanelScopeContext);
}
